// Settings — BYOK key management & provider selection (PRD 006). Keys are read
// from and written to IndexedDB only; nothing here touches a first-party origin.
// Renders the first-run privacy disclosure (F4) and the clear-all path (F8).

import { Check, ShieldCheck, TriangleAlert, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { ReadingColumn } from '@/components/shell/AppShell'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Switch } from '@/components/ui/Switch'
import { cn } from '@/lib/utils'
import { clearAllData, loadKeys, saveKeys } from '@/lib/keys/keyStore'
import { resolveBridgeEndpoint } from '@/lib/bridge/config'
import {
  testBridge,
  testTranscriptionKey,
  type KeyTestResult
} from '@/lib/keys/validate'
import {
  DEFAULT_KEYS,
  PROVIDER_LABELS,
  type ProviderId,
  type SpeechmaticsRegion,
  type StoredKeys
} from '@/lib/keys/types'
import { play, synthesize } from '@/lib/ai/tts'
import {
  SPEAKABLE_KINDS,
  getSpokenKinds,
  setSpokenKinds
} from '@/lib/ai/speech'
import type { AiTurnKind } from '@/lib/ai/types'

const PROVIDERS: ProviderId[] = ['speechmatics', 'soniox']
const REGIONS: SpeechmaticsRegion[] = ['eu', 'us']

export function Settings() {
  const [keys, setKeys] = useState<StoredKeys>(DEFAULT_KEYS)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    loadKeys().then((k) => {
      setKeys(k)
      setLoaded(true)
    })
  }, [])

  // Persist a patch and reflect it locally. Settings are small and saving on
  // every change keeps the BYOK record the single source of truth.
  function update(patch: Partial<StoredKeys>) {
    setKeys((prev) => ({ ...prev, ...patch }))
    void saveKeys(patch)
  }

  if (!loaded) {
    return (
      <ReadingColumn>
        <p className="text-body-sm text-muted-foreground">Loading settings…</p>
      </ReadingColumn>
    )
  }

  return (
    <ReadingColumn>
      <p className="text-body-sm text-muted-foreground">Settings</p>
      <h1 className="mt-1 font-serif text-display-md text-text">Keys & provider</h1>

      <Disclosure
        acceptedAt={keys.disclosureAcceptedAt}
        provider={keys.provider}
        onAccept={() => update({ disclosureAcceptedAt: Date.now() })}
      />

      <Section title="Transcription provider">
        <div className="flex gap-2">
          {PROVIDERS.map((p) => (
            <Button
              key={p}
              size="sm"
              variant={keys.provider === p ? 'default' : 'outline'}
              onClick={() => update({ provider: p })}
            >
              {PROVIDER_LABELS[p]}
            </Button>
          ))}
        </div>

        <KeyField
          label={`${PROVIDER_LABELS[keys.provider]} API key`}
          value={keys.transcriptionKey ?? ''}
          onChange={(v) => update({ transcriptionKey: v || null })}
          test={() =>
            testTranscriptionKey(
              keys.provider,
              keys.transcriptionKey ?? '',
              keys.speechmaticsRegion
            )
          }
        />

        {keys.provider === 'speechmatics' && (
          <Field label="Region">
            <div className="flex gap-2">
              {REGIONS.map((r) => (
                <Button
                  key={r}
                  size="sm"
                  variant={keys.speechmaticsRegion === r ? 'default' : 'outline'}
                  onClick={() => update({ speechmaticsRegion: r })}
                >
                  {r.toUpperCase()}
                </Button>
              ))}
            </div>
          </Field>
        )}

        {keys.provider === 'soniox' && (
          <ToggleRow
            label="Direct-key path"
            hint="Send your key on the socket instead of minting a temp key. Unproven — leave off unless you know you need it."
            checked={keys.sonioxDirectKey}
            onChange={(v) => update({ sonioxDirectKey: v })}
          />
        )}
      </Section>

      <Section title="AI participant — Claude Code bridge">
        <Field label="Bridge URL">
          <Input
            value={keys.bridgeUrl}
            onChange={(e) => update({ bridgeUrl: e.target.value })}
            placeholder="same origin (leave blank)"
          />
          <span className="mt-1 block text-label text-muted-foreground">
            Leave blank when you open the room from <span className="text-text">/brainstorm</span>{' '}
            — the gateway serves this page, so it pairs to its own origin. Set it
            only when running the dev server on a different port.
          </span>
        </Field>
        <KeyField
          label="Pairing token"
          value={keys.bridgeToken ?? ''}
          onChange={(v) => update({ bridgeToken: v || null })}
          test={() =>
            testBridge(
              resolveBridgeEndpoint(keys.bridgeUrl) || window.location.origin,
              keys.bridgeToken ?? ''
            )
          }
        />
        <Field label="Address name">
          <Input
            value={keys.aiTriggerName}
            onChange={(e) => update({ aiTriggerName: e.target.value })}
            placeholder="Claude"
          />
          <span className="mt-1 block text-label text-muted-foreground">
            What the room calls the AI to address it ("Claude, research …"). The
            AI answers inline automatically. Pick something that won't collide
            with "covariance" when spoken.
          </span>
        </Field>
        <p className="text-label text-muted-foreground">
          The AI participant is your own Claude Code over a local bridge — no
          Anthropic key in the browser. The easy path: run{' '}
          <span className="text-text">/brainstorm</span> in Claude Code and open
          the link it gives you — this page opens already paired. These fields
          are the manual fallback (e.g. the dev server).
        </p>
      </Section>

      <Section title="AI voice — ElevenLabs (optional)">
        <p className="text-label text-muted-foreground">
          Give the AI a spoken voice. With a key set, each contribution gets a
          speak button, and you can turn on <span className="text-text">Auto-speak</span>{' '}
          in the session footer so the AI claims a pause to talk. Browser-direct,
          BYOK — the key stays in this browser.
        </p>
        <VoiceSection keys={keys} update={update} />
      </Section>

      <Section title="Local data">
        <p className="text-body-sm text-muted-foreground">
          Keys and sessions live only in this browser. Clearing wipes them for
          good.
        </p>
        <ClearAll
          onCleared={() => {
            setKeys({ ...DEFAULT_KEYS })
          }}
        />
      </Section>
    </ReadingColumn>
  )
}

function Disclosure({
  acceptedAt,
  provider,
  onAccept
}: {
  acceptedAt: number | null
  provider: ProviderId
  onAccept: () => void
}) {
  return (
    <div className="mt-6 rounded-lg border border-border bg-surface p-4">
      <div className="flex items-center gap-2">
        <ShieldCheck className="size-4 text-accent" />
        <span className="text-body-sm font-medium text-text">
          What leaves this machine
        </span>
        {acceptedAt && (
          <Badge variant="outline" className="ml-auto">
            acknowledged
          </Badge>
        )}
      </div>
      <p className="mt-2 text-body-sm text-muted-foreground">
        Your microphone audio is sent to{' '}
        <span className="text-text">{PROVIDER_LABELS[provider]}</span> for
        transcription. Your transcript is sent to your own{' '}
        <span className="text-text">Claude Code</span> over a local bridge on
        this machine. Nothing is sent to us — there is no server. Your keys stay
        in this browser.
      </p>
      {!acceptedAt && (
        <Button size="sm" className="mt-3" onClick={onAccept}>
          I understand
        </Button>
      )}
    </div>
  )
}

function Section({
  title,
  children
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="mt-8 space-y-3">
      <h2 className="text-heading-3 font-medium text-text">{title}</h2>
      {children}
    </section>
  )
}

function Field({
  label,
  children
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <span className="mb-1 block text-label text-muted-foreground">{label}</span>
      {children}
    </div>
  )
}

function KeyField({
  label,
  value,
  onChange,
  test
}: {
  label: string
  value: string
  onChange: (v: string) => void
  test: () => Promise<KeyTestResult>
}) {
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<KeyTestResult | null>(null)

  async function runTest() {
    setBusy(true)
    setResult(null)
    try {
      setResult(await test())
    } catch (err) {
      setResult({
        ok: false,
        detail: err instanceof Error ? err.message : String(err)
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Field label={label}>
      <div className="flex gap-2">
        <Input
          type="password"
          autoComplete="off"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="paste key"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={busy || !value}
          onClick={runTest}
        >
          {busy ? 'testing…' : 'test'}
        </Button>
      </div>
      {result && (
        <p
          className={cn(
            'mt-1.5 flex items-center gap-1.5 text-label',
            result.ok ? 'text-success' : 'text-danger'
          )}
        >
          {result.ok ? (
            <Check className="size-3.5" />
          ) : (
            <X className="size-3.5" />
          )}
          {result.detail}
        </p>
      )}
    </Field>
  )
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange
}: {
  label: string
  hint: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <span className="block text-body-sm text-text">{label}</span>
        <span className="block text-label text-muted-foreground">{hint}</span>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  )
}

const KIND_LABEL: Record<AiTurnKind, string> = {
  present: 'Present',
  caution: 'Caution',
  insight: 'Insight',
  candidate: 'Idea',
  note: 'Note'
}

// The ElevenLabs voice config: key + voice + output routing + auto-speak prefs.
// The key/voice/device live in the BYOK record (IndexedDB); the auto-speak prefs
// (which kinds) are localStorage, shared live with the session UI.
function VoiceSection({
  keys,
  update
}: {
  keys: StoredKeys
  update: (patch: Partial<StoredKeys>) => void
}) {
  const [outputs, setOutputs] = useState<MediaDeviceInfo[]>([])
  const [kinds, setKinds] = useState<AiTurnKind[]>(() => getSpokenKinds())
  const [previewing, setPreviewing] = useState(false)

  useEffect(() => {
    navigator.mediaDevices
      ?.enumerateDevices?.()
      .then((ds) => setOutputs(ds.filter((d) => d.kind === 'audiooutput')))
      .catch(() => {})
  }, [])

  function toggleKind(k: AiTurnKind) {
    const next = kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k]
    setKinds(next)
    setSpokenKinds(next)
  }

  async function preview() {
    if (!keys.elevenLabsKey) return
    setPreviewing(true)
    try {
      const blob = await synthesize(
        'This is the voice the AI participant will use in the room.',
        { apiKey: keys.elevenLabsKey, voiceId: keys.ttsVoiceId }
      )
      await play(blob, keys.ttsOutputDeviceId || undefined).done
    } catch {
      /* error surfaces via the key test button */
    } finally {
      setPreviewing(false)
    }
  }

  return (
    <>
      <KeyField
        label="ElevenLabs API key"
        value={keys.elevenLabsKey ?? ''}
        onChange={(v) => update({ elevenLabsKey: v || null })}
        test={() =>
          synthesize('Voice check.', {
            apiKey: keys.elevenLabsKey ?? '',
            voiceId: keys.ttsVoiceId
          })
            .then<KeyTestResult>(() => ({ ok: true, detail: 'Voice works.' }))
            .catch<KeyTestResult>((e) => ({
              ok: false,
              detail: e instanceof Error ? e.message : String(e)
            }))
        }
      />

      <Field label="Voice ID">
        <div className="flex gap-2">
          <Input
            value={keys.ttsVoiceId}
            onChange={(e) => update({ ttsVoiceId: e.target.value })}
            placeholder="ElevenLabs voice id"
          />
          <Button
            size="sm"
            variant="outline"
            disabled={!keys.elevenLabsKey || previewing}
            onClick={preview}
          >
            {previewing ? 'playing…' : 'preview'}
          </Button>
        </div>
        <span className="mt-1 block text-label text-muted-foreground">
          From your ElevenLabs voice library. Preview plays through the output
          device below.
        </span>
      </Field>

      <Field label="Voice output device">
        <select
          value={keys.ttsOutputDeviceId}
          onChange={(e) => update({ ttsOutputDeviceId: e.target.value })}
          className="h-9 w-full rounded-md border border-border bg-background px-2.5 text-body-sm text-text"
        >
          <option value="">System default (your speakers)</option>
          {outputs.map((d, i) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || `Output ${i + 1}`}
            </option>
          ))}
        </select>
        <span className="mt-1 block text-label text-muted-foreground">
          To let remote participants on a Meet/Zoom tab hear the AI, install a
          virtual cable (e.g. <span className="text-text">BlackHole</span>), pick it
          as Meet’s microphone, and select it here. Use a multi-output device if
          you also want to hear the AI locally.
        </span>
      </Field>

      <Field label="Auto-speak — voice these aloud">
        <div className="flex flex-wrap gap-2">
          {SPEAKABLE_KINDS.map((k) => (
            <Button
              key={k}
              size="sm"
              variant={kinds.includes(k) ? 'default' : 'outline'}
              onClick={() => toggleKind(k)}
            >
              {KIND_LABEL[k]}
            </Button>
          ))}
        </div>
        <span className="mt-1 block text-label text-muted-foreground">
          Which contributions the AI speaks when Voice is on. A card is spoken as
          it pops in the focus modal, where you can mute or dismiss it. The AI can
          also force or suppress speech per message. Manual speak buttons work for
          any card regardless.
        </span>
      </Field>
    </>
  )
}

function ClearAll({ onCleared }: { onCleared: () => void }) {
  const [confirming, setConfirming] = useState(false)

  if (!confirming) {
    return (
      <Button
        size="sm"
        variant="destructive"
        onClick={() => setConfirming(true)}
      >
        <TriangleAlert /> Clear all keys and local data
      </Button>
    )
  }
  return (
    <div className="flex items-center gap-2">
      <span className="text-body-sm text-text">Wipe everything?</span>
      <Button
        size="sm"
        variant="destructive"
        onClick={async () => {
          await clearAllData()
          setConfirming(false)
          onCleared()
        }}
      >
        Yes, clear
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
        Cancel
      </Button>
    </div>
  )
}
